import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { LanguageContext } from '../../providers/LanguageProvider';
import authService from '../../lib/authService';
import { getWidgetEdgeToken } from '../../lib/edgeTokenService';
import { resolveWidgetMintBase } from '../../lib/gatewayEndpoint';
import { featureFlagsContext } from '../../providers/FeatureFlagsProvider';

export interface WidgetHostProps {
  type: 'bill-comparison' | 'energy-usage' | 'rate-compare';
  identifier: string;
  personId: string;
  isMobile?: boolean;
  displayEnergyUsageDownloadButton?: boolean;
}

// const WIDGET_HOST = 'http://localhost:5173/';
const WIDGET_HOST = process.env.GATSBY_PGE_WIDGETS_ORIGIN || '';
const WIDGET_ORIGIN = new URL(WIDGET_HOST).origin;

const isAllowedNavigationUrl = (value: unknown): value is string => {
  if (typeof value !== 'string' || !value.trim()) {
    return false;
  }

  try {
    const url = new URL(value, window.location.origin);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

const getSessionToken = () => {
  try {
    return authService.tokensStorage?.get()?.accessToken?.token ?? '';
  } catch {
    return '';
  }
};

/**
 * The token handed to the widget.
 *
 * The widget presents it to whatever gateway cs-widgets was *built* against — a
 * `VITE_API_URL` in another repo that nothing here can see. So the widget reports
 * that origin in its `ready` handshake and we mint to match. When it matches where
 * the session token was already minted, `resolveWidgetMintBase` returns null and we
 * reuse the session token exactly as before; when it differs we mint a dedicated
 * widget token at the gateway the widget will actually talk to. Getting this wrong
 * in either direction is a real prod incident: an Apigee token at api-edge 403s
 * (2026-09-04), an api-edge token at Apigee 401s.
 *
 * Resolves to '' rather than throwing: the widget treats a missing token as "not
 * ready yet" and asks again via `token-expiring`, so a failed mint degrades instead
 * of breaking the iframe.
 */
const resolveWidgetToken = async (
  apiOrigin: string | null,
  useEdgeToken: boolean,
): Promise<string> => {
  const mintBase = resolveWidgetMintBase(apiOrigin, useEdgeToken);
  if (!mintBase) {
    return getSessionToken();
  }
  try {
    const tokens = await getWidgetEdgeToken(mintBase);
    return tokens.accessToken?.token ?? '';
  } catch {
    return '';
  }
};

export default function WidgetHost({
  type,
  identifier,
  personId,
  isMobile = false,
  displayEnergyUsageDownloadButton = false,
}: WidgetHostProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIframeHeight] = useState<number | string>('100%');
  const { language } = useContext(LanguageContext);
  // Read through the React context, NOT the gatewayEndpoint singleton. The singleton
  // starts `false` and only becomes true when Firebase's onValue fires, so a mount
  // effect reading it raced the network and always took the legacy branch — the flag
  // could be true in Firebase and the edge mint would never happen. The context value
  // updates, so it can drive the dependency array below.
  const { useEdgeWidgetToken, loading: flagsLoading } = useContext(
    featureFlagsContext,
  );
  // Not `useState(resolveWidgetToken)`: a lazy initializer returning a Promise would
  // put a Promise in state, and postMessage throws DataCloneError on one — taking out
  // both `init` and `update-token`. Seeded empty, filled by the effect below.
  const [authtoken, setAuthtoken] = useState('');
  // Null until the iframe says `ready`. Holds the gateway the widget reported, so
  // the token is minted where it will be presented rather than where a flag guesses.
  // `apiOrigin` is null for widget builds predating the handshake, which falls back
  // to the useEdgeWidgetToken flag — so this is a no-op until cs-widgets ships it.
  const [widgetGateway, setWidgetGateway] = useState<{
    apiOrigin: string | null;
  } | null>(null);

  const sendInit = useCallback(() => {
    iframeRef.current?.contentWindow?.postMessage(
      {
        type: 'init',
        config: {
          type,
          identifier,
          personId,
          authtoken,
          language,
          isMobile,
          displayEnergyUsageDownloadButton:
            type === 'energy-usage' && displayEnergyUsageDownloadButton,
        },
      },
      WIDGET_ORIGIN,
    );
  }, [
    type,
    identifier,
    personId,
    authtoken,
    language,
    isMobile,
    displayEnergyUsageDownloadButton,
  ]);

  const sendToken = useCallback((token: string) => {
    iframeRef.current?.contentWindow?.postMessage(
      {
        type: 'update-token',
        authtoken: token,
      },
      WIDGET_ORIGIN,
    );
  }, []);

  const scrollWidgetToTop = useCallback(() => {
    const iframe = iframeRef.current;

    if (!iframe) {
      window.scrollTo({ top: 0, left: 0 });
      return;
    }

    const top = iframe.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ top, left: 0 });
  }, []);

  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      if (e.origin !== WIDGET_ORIGIN) {
        return;
      }
      if (e.source !== iframeRef.current?.contentWindow) {
        return;
      }

      switch (e.data?.type) {
        case 'ready': {
          const reported =
            typeof e.data?.apiOrigin === 'string' ? e.data.apiOrigin : null;
          // Keep the object identity stable when nothing changed, so a widget that
          // re-announces `ready` cannot retrigger the resolve effect below.
          setWidgetGateway(prev =>
            prev && prev.apiOrigin === reported
              ? prev
              : { apiOrigin: reported },
          );
          sendInit();
          break;
        }

        case 'token-expiring': {
          // Fire-and-forget: making the listener async would change its identity
          // contract. edgeTokenService dedupes concurrent mints, so a widget that
          // emits this repeatedly cannot storm the token endpoint.
          void resolveWidgetToken(
            widgetGateway?.apiOrigin ?? null,
            useEdgeWidgetToken,
          ).then(freshToken => {
            sendToken(freshToken);
            setAuthtoken(freshToken);
          });

          break;
        }

        case 'resize':
          if (e.data.height) {
            setIframeHeight(e.data.height);
          }
          break;

        case 'scroll-to-top':
          scrollWidgetToTop();
          break;

        case 'navigate':
          if (isAllowedNavigationUrl(e.data.url)) {
            window.location.assign(e.data.url);
          }
          break;

        default:
          break;
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [
    sendInit,
    sendToken,
    scrollWidgetToTop,
    useEdgeWidgetToken,
    widgetGateway,
  ]);

  useEffect(() => {
    // Wait for BOTH the flags and the widget's `ready` before resolving. Resolving
    // early and re-resolving on change would hand the widget a token minted at the
    // wrong gateway first — a 403 or 401 on its opening query — before replacing it.
    // `ready` is sent unconditionally from the widget's mount effect in every build,
    // so this cannot strand a widget that would otherwise have been served.
    if (flagsLoading || !widgetGateway) {
      return undefined;
    }

    let cancelled = false;

    void resolveWidgetToken(widgetGateway.apiOrigin, useEdgeWidgetToken).then(
      token => {
        if (!cancelled) {
          setAuthtoken(token);
        }
      },
    );

    return () => {
      cancelled = true;
    };
  }, [widgetGateway, useEdgeWidgetToken, flagsLoading]);

  useEffect(() => {
    // `init` may already have shipped an empty authtoken if the mint had not resolved
    // by the time the iframe said `ready`. That is intentional: the widget gates its
    // queries on the token and picks up this `update-token` instead, so first render
    // is never blocked on a network round trip.
    if (authtoken) {
      sendToken(authtoken);
    }
  }, [authtoken, sendToken]);

  return (
    <iframe
      ref={iframeRef}
      src={WIDGET_HOST + type}
      sandbox="allow-scripts allow-same-origin allow-forms allow-downloads"
      onLoad={sendInit}
      scrolling="no"
      style={{
        width: '100%',
        height:
          typeof iframeHeight === 'number' ? `${iframeHeight}px` : iframeHeight,
        border: 'none',
        display: 'block',
        transition: 'height 0.2s ease-in-out',
      }}
    />
  );
}
